import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, AreaChart, Area,
} from "recharts";
import { LuLock, LuUser } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";

const PERIODS = [
  { key: "quarter", label: "Last 3 Months" },
  { key: "year", label: "This Year" },
  { key: "all", label: "All Time" },
];

function periodCutoff(key) {
  const now = new Date();
  if (key === "quarter") return new Date(now.getFullYear(), now.getMonth() - 2, 1);
  if (key === "year") return new Date(now.getFullYear(), 0, 1);
  return null;
}

function monthKey(ts) {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function localDateKey(ts) {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtAUD(v) {
  if (!v) return "$0";
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(v);
}

function fmtTime(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function fmtDisplayDate(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function fmtDuration(onTs, offTs) {
  if (!onTs || !offTs) return null;
  const mins = Math.round((new Date(offTs) - new Date(onTs)) / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function getMonthBins(period) {
  const now = new Date();
  const bins = [];
  if (period === "year") {
    for (let m = 0; m <= now.getMonth(); m++) {
      const d = new Date(now.getFullYear(), m, 1);
      bins.push({ key: `${now.getFullYear()}-${String(m + 1).padStart(2, "0")}`, label: d.toLocaleString("en-AU", { month: "short" }) });
    }
  } else if (period === "quarter") {
    for (let i = 2; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      bins.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString("en-AU", { month: "short", year: "2-digit" }) });
    }
  } else {
    // All time — derive bins from actual data, up to 12 most recent months
    return null; // signal to caller to build from data
  }
  return bins;
}

function KpiCard({ label, value, sub, accent, locked }) {
  return (
    <div style={{
      backgroundColor: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${accent || colors.teal}`,
      borderRadius: "8px",
      padding: "16px 18px",
      flex: 1,
      minWidth: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
        <span style={{ fontSize: "10px", fontWeight: "700", color: colors.slate, letterSpacing: "0.06em", textTransform: "uppercase" }}>
          {label}
        </span>
        {locked && <LuLock size={10} color={colors.slate} />}
      </div>
      <div style={{ fontSize: "26px", fontWeight: "800", color: colors.darkGrey, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: "11px", color: colors.slate, marginTop: "4px" }}>{sub}</div>}
    </div>
  );
}

export function StaffRecords() {
  const isMobile = useIsMobile();
  const [period, setPeriod] = useState("quarter");
  const [techs, setTechs] = useState([]);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [siteMap, setSiteMap] = useState({});
  const [selectedTechId, setSelectedTechId] = useState(null); // null = all
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/technicians/`),
      axios.get(`${API}/attendance-log/`),
      axios.get(`${API}/inspections/`),
      axios.get(`${API}/service-requests/`),
      axios.get(`${API}/sites/`),
    ])
      .then(([techRes, logRes, inspRes, srRes, siteRes]) => {
        setTechs(techRes.data);
        setAttendanceLogs(logRes.data);
        setInspections(inspRes.data);
        setServiceRequests(srRes.data);
        const map = {};
        siteRes.data.forEach(s => { map[s.id] = s; });
        setSiteMap(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cutoff = periodCutoff(period);
  const inPeriod = (ts) => !cutoff || new Date(ts) >= cutoff;

  // Filter all data to selected tech + period
  const logs = useMemo(() => {
    return attendanceLogs.filter(l =>
      (!selectedTechId || l.tech === selectedTechId) && inPeriod(l.timestamp)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceLogs, selectedTechId, period]);

  const completedInspections = useMemo(() => {
    return inspections.filter(i =>
      (i.status === "passed" || i.status === "failed") &&
      i.completed_at &&
      (!selectedTechId || i.assigned_to === selectedTechId) &&
      inPeriod(i.completed_at)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspections, selectedTechId, period]);

  const completedSRs = useMemo(() => {
    return serviceRequests.filter(sr =>
      sr.status === "complete" &&
      sr.completed_at &&
      (!selectedTechId || sr.assigned_to === selectedTechId) &&
      inPeriod(sr.completed_at)
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceRequests, selectedTechId, period]);

  // Revenue from completed inspections: use site fee for inspection type
  const totalValue = useMemo(() => {
    return completedInspections.reduce((sum, i) => {
      const site = siteMap[i.site];
      if (!site) return sum;
      const fee = i.inspection_type === "monthly"
        ? parseFloat(site.monthly_fee || 0)
        : i.inspection_type === "bi_annual"
        ? parseFloat(site.bi_annual_fee || 0)
        : parseFloat(site.annual_fee || 0);
      return sum + fee;
    }, 0);
  }, [completedInspections, siteMap]);

  // Attendance history: pair clock-on/off by local date
  const attendanceRows = useMemo(() => {
    const byDate = {};
    logs.forEach(l => {
      const dk = localDateKey(l.timestamp);
      if (!byDate[dk]) byDate[dk] = { date: dk, onTs: null, offTs: null, lat: null, lng: null };
      if (l.event === "clock_on") {
        if (!byDate[dk].onTs || l.timestamp < byDate[dk].onTs) {
          byDate[dk].onTs = l.timestamp;
          byDate[dk].lat = l.latitude;
          byDate[dk].lng = l.longitude;
        }
      } else {
        if (!byDate[dk].offTs || l.timestamp > byDate[dk].offTs) {
          byDate[dk].offTs = l.timestamp;
        }
      }
    });
    return Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));
  }, [logs]);

  // Monthly chart data
  const chartData = useMemo(() => {
    let bins = getMonthBins(period);
    if (!bins) {
      // All time: collect unique months from data
      const months = new Set();
      completedInspections.forEach(i => months.add(monthKey(i.completed_at)));
      completedSRs.forEach(s => months.add(monthKey(s.completed_at)));
      if (months.size === 0) return [];
      const sorted = Array.from(months).sort();
      // Cap at 12 most recent
      const recent = sorted.slice(-12);
      bins = recent.map(k => {
        const [y, m] = k.split("-").map(Number);
        return { key: k, label: new Date(y, m - 1, 1).toLocaleString("en-AU", { month: "short", year: "2-digit" }) };
      });
    }

    return bins.map(bin => {
      const insps = completedInspections.filter(i => monthKey(i.completed_at) === bin.key).length;
      const srs = completedSRs.filter(s => monthKey(s.completed_at) === bin.key).length;
      const value = completedInspections
        .filter(i => monthKey(i.completed_at) === bin.key)
        .reduce((sum, i) => {
          const site = siteMap[i.site];
          if (!site) return sum;
          const fee = i.inspection_type === "monthly"
            ? parseFloat(site.monthly_fee || 0)
            : i.inspection_type === "bi_annual"
            ? parseFloat(site.bi_annual_fee || 0)
            : parseFloat(site.annual_fee || 0);
          return sum + fee;
        }, 0);
      return { month: bin.label, Inspections: insps, "Service Requests": srs, Value: Math.round(value) };
    });
  }, [completedInspections, completedSRs, siteMap, period]);

  // Selected tech name
  const selectedTech = techs.find(t => t.id === selectedTechId);

  const pad = isMobile ? "12px" : "24px";

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: colors.slate, fontSize: "14px" }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", padding: pad }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "20px" }}>
        <h1 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: colors.darkGrey }}>Staff Records</h1>
        <div style={{ display: "flex", gap: "6px" }}>
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: "6px 14px",
                borderRadius: "20px",
                border: `1px solid ${period === p.key ? colors.teal : colors.border}`,
                backgroundColor: period === p.key ? colors.teal : colors.white,
                color: period === p.key ? "white" : colors.slate,
                fontSize: "12px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tech selector */}
      <div style={{
        backgroundColor: colors.white,
        border: `1px solid ${colors.border}`,
        borderRadius: "10px",
        padding: "14px 16px",
        marginBottom: "20px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
      }}>
        <span style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, marginRight: "4px" }}>Tech:</span>
        <button
          onClick={() => setSelectedTechId(null)}
          style={{
            padding: "5px 14px",
            borderRadius: "20px",
            border: `1px solid ${!selectedTechId ? colors.darkGrey : colors.border}`,
            backgroundColor: !selectedTechId ? colors.darkGrey : "transparent",
            color: !selectedTechId ? "white" : colors.slate,
            fontSize: "12px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          All Techs
        </button>
        {techs.map(t => (
          <button
            key={t.id}
            onClick={() => setSelectedTechId(selectedTechId === t.id ? null : t.id)}
            style={{
              padding: "5px 14px",
              borderRadius: "20px",
              border: `1px solid ${selectedTechId === t.id ? colors.teal : colors.border}`,
              backgroundColor: selectedTechId === t.id ? colors.teal : "transparent",
              color: selectedTechId === t.id ? "white" : colors.slate,
              fontSize: "12px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <LuUser size={12} />
            {t.first_name ? `${t.first_name} ${t.last_name}`.trim() : t.username}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <KpiCard
          label="Inspections Completed"
          value={completedInspections.length}
          sub={`${selectedTech ? selectedTech.first_name || selectedTech.username : "All techs"} · ${PERIODS.find(p => p.key === period)?.label}`}
          accent={colors.teal}
        />
        <KpiCard
          label="Service Requests Completed"
          value={completedSRs.length}
          sub={`${selectedTech ? selectedTech.first_name || selectedTech.username : "All techs"} · ${PERIODS.find(p => p.key === period)?.label}`}
          accent={colors.green}
        />
        <KpiCard
          label="Value Delivered"
          value={fmtAUD(totalValue)}
          sub="From inspection fees"
          accent={colors.amber}
          locked
        />
      </div>

      {/* Mid row: Attendance + Completions chart */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
        gap: "16px",
        marginBottom: "16px",
      }}>

        {/* Attendance history */}
        <div style={{ backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: "10px", overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${colors.border}` }}>
            <span style={{ fontSize: "13px", fontWeight: "700", color: colors.darkGrey }}>Attendance History</span>
          </div>
          <div style={{ overflowY: "auto", maxHeight: "340px" }}>
            {attendanceRows.length === 0 ? (
              <div style={{ padding: "32px", textAlign: "center", color: colors.slate, fontSize: "13px" }}>
                No attendance records for this period
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc" }}>
                    {["Date", "Clock On", "Clock Off", "Duration"].map(h => (
                      <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontWeight: "600", color: colors.slate, fontSize: "11px", borderBottom: `1px solid ${colors.border}` }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {attendanceRows.map((row, i) => {
                    const duration = fmtDuration(row.onTs, row.offTs);
                    return (
                      <tr key={row.date} style={{ borderBottom: i < attendanceRows.length - 1 ? `1px solid ${colors.border}` : "none" }}>
                        <td style={{ padding: "9px 14px", color: colors.darkGrey, fontWeight: "500" }}>{fmtDisplayDate(row.date)}</td>
                        <td style={{ padding: "9px 14px", color: row.onTs ? colors.green : colors.slate }}>
                          {row.onTs ? fmtTime(row.onTs) : "—"}
                        </td>
                        <td style={{ padding: "9px 14px", color: row.offTs ? colors.slate : "#fbbf24" }}>
                          {row.offTs ? fmtTime(row.offTs) : "On duty"}
                        </td>
                        <td style={{ padding: "9px 14px", color: colors.slate }}>
                          {duration || "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Completions chart */}
        <div style={{ backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: "10px", overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: `1px solid ${colors.border}` }}>
            <span style={{ fontSize: "13px", fontWeight: "700", color: colors.darkGrey }}>Completions by Month</span>
          </div>
          <div style={{ padding: "16px", height: "340px" }}>
            {chartData.length === 0 ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: colors.slate, fontSize: "13px" }}>
                No completions data for this period
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: colors.slate }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: colors.slate }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: "12px", borderRadius: "6px", border: `1px solid ${colors.border}` }}
                    cursor={{ fill: "#f1f5f9" }}
                  />
                  <Legend wrapperStyle={{ fontSize: "11px" }} />
                  <Bar dataKey="Inspections" fill={colors.teal} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Service Requests" fill={colors.green} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Revenue chart — management eyes only */}
      <div style={{ backgroundColor: colors.white, border: `1px solid ${colors.border}`, borderRadius: "10px", overflow: "hidden" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${colors.border}`, display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "13px", fontWeight: "700", color: colors.darkGrey }}>Value Delivered by Month</span>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            backgroundColor: "#fef3c7", color: "#92400e",
            fontSize: "10px", fontWeight: "700", padding: "2px 8px", borderRadius: "12px",
            letterSpacing: "0.04em",
          }}>
            <LuLock size={9} /> MANAGEMENT ONLY
          </span>
        </div>
        <div style={{ padding: "16px", height: "260px" }}>
          {chartData.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: colors.slate, fontSize: "13px" }}>
              No data for this period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="valueGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={colors.amber} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={colors.amber} stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.border} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: colors.slate }} axisLine={false} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.slate }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{ fontSize: "12px", borderRadius: "6px", border: `1px solid ${colors.border}` }}
                  formatter={v => [fmtAUD(v), "Value"]}
                  cursor={{ stroke: colors.amber, strokeWidth: 1, strokeDasharray: "4 2" }}
                />
                <Area
                  type="monotone"
                  dataKey="Value"
                  stroke={colors.amber}
                  strokeWidth={2.5}
                  fill="url(#valueGrad)"
                  dot={{ r: 4, fill: colors.amber, strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

    </div>
  );
}
